import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-escrow-password-form.css?inline';
import { encryptPem, MIN_RECOVERY_PASSWORD_LENGTH, type EncryptedEscrow } from '../../crypto/recovery-crypto.js';
import { requestThroughGate } from '../../lib/step-up.js';
import '../pk-alert/pk-alert.js';
import '../pk-button/pk-button.js';

/** The warning every escrow form shows. */
export const ESCROW_WARNING =
    'We cannot decrypt it for you. The copy is locked in your browser with this recovery password before it is sent; we never see the password or the key. If you forget the password, the copy is useless — to you and to us.';

/** Validates the form's inputs; returns the error text or '' when they are fine. */
export function validateEscrowInput(secret: string, password: string, confirm: string): string {
    if (!secret.trim()) return 'Paste the private key (PEM) or choose the file.';
    if (password.length < MIN_RECOVERY_PASSWORD_LENGTH) return `The recovery password must be at least ${MIN_RECOVERY_PASSWORD_LENGTH} characters.`;
    if (password !== confirm) return 'The two passwords do not match.';
    return '';
}

/**
 * `<pk-escrow-password-form>` — sets the escrow copy of a customer-held private key: the key (paste the PEM or
 * choose the file), a recovery password and its confirmation (min 8 characters), and the "we cannot decrypt it for
 * you" warning. On submit it encrypts **in the browser** with `@umbricians/portal-kit/crypto` (PBKDF2-SHA256 600k →
 * AES-256-GCM) and emits the encrypted blob; the plaintext inputs have no `name` and are cleared, so the key and the
 * password never leave the page.
 *
 * With `action` set, it then POSTs `encryptedBlob` + `escrowParams` (JSON) there — through an enclosing
 * `<pk-step-up-gate>` when there is one, else as a plain form.
 *
 * Attributes:
 *   - `action`        — POST URL for the encrypted copy (optional; without it only the event fires).
 *   - `key-id`        — the key (in the event detail).
 *   - `submit-label`  — button text (default `Store the escrow copy`).
 *   - `error`         — server error text.
 *   - `csrf-token` / `csrf-name` — anti-forgery field of the POST.
 *
 * Events:
 *   - `pk-escrow-encrypted` — `CustomEvent<{ keyId, encryptedBlob, escrowParams }>`, cancelable (cancel = do not POST).
 */
export class PkEscrowPasswordForm extends HTMLElement {
    static readonly tagName = 'pk-escrow-password-form';

    static get observedAttributes(): string[] {
        return ['action', 'key-id', 'submit-label', 'error', 'csrf-token', 'csrf-name'];
    }

    private readonly _root: ShadowRoot;
    private _error = '';
    private _busy = false;
    private _done = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._root.addEventListener('submit', this._onSubmit);
        this._render();
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._onSubmit);
    }

    attributeChangedCallback(): void {
        if (this.isConnected) this._render();
    }

    private _input(name: string): HTMLInputElement | HTMLTextAreaElement | null {
        return this._root.querySelector(`[data-field="${name}"]`);
    }

    private readonly _onFile = async (e: Event): Promise<void> => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        const text = await file.text();
        const secret = this._input('secret');
        if (secret) secret.value = text;
    };

    private readonly _onSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        if (!form.classList.contains('escrow')) return;
        e.preventDefault(); // never a native post of the plaintext form
        void this._encryptAndSend();
    };

    private async _encryptAndSend(): Promise<void> {
        const secret = this._input('secret')?.value ?? '';
        const password = this._input('password')?.value ?? '';
        const confirm = this._input('confirm')?.value ?? '';
        this._error = validateEscrowInput(secret, password, confirm);
        if (this._error) {
            this._render();
            return;
        }

        this._busy = true;
        this._render();
        let encrypted: EncryptedEscrow;
        try {
            encrypted = await encryptPem(secret, password);
        } catch (err) {
            this._busy = false;
            this._error = err instanceof Error ? err.message : 'Encryption failed.';
            this._render();
            return;
        }
        this._clearInputs();

        const keyId = this.getAttribute('key-id') ?? '';
        const proceed = this.dispatchEvent(new CustomEvent('pk-escrow-encrypted', {
            bubbles: true, composed: true, cancelable: true, detail: { keyId, ...encrypted },
        }));
        const action = this.getAttribute('action') ?? '';
        if (!proceed || !action) {
            this._busy = false;
            this._done = true;
            this._render();
            return;
        }

        const fields: Record<string, string> = {
            encryptedBlob: encrypted.encryptedBlob,
            escrowParams: JSON.stringify(encrypted.escrowParams),
        };
        const token = this.getAttribute('csrf-token') ?? '';
        if (token) fields[this.getAttribute('csrf-name') ?? '__RequestVerificationToken'] = token;
        const routed = requestThroughGate(this, { url: action, fields, label: `Store the escrow copy of ${keyId}` });
        if (routed) {
            const outcome = await routed;
            this._busy = false;
            this._done = outcome.ok;
            this._render();
            return;
        }
        this._postNatively(action, fields);
    }

    private _postNatively(action: string, fields: Record<string, string>): void {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = action;
        form.style.display = 'none';
        for (const [name, value] of Object.entries(fields)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        }
        this._root.appendChild(form);
        form.submit();
    }

    private _clearInputs(): void {
        for (const name of ['secret', 'password', 'confirm', 'file']) {
            const input = this._input(name);
            if (input) input.value = '';
        }
    }

    private template(): TemplateResult {
        const error = this._error || this.getAttribute('error') || '';
        return html`
            <style>${styles}</style>
            <pk-alert variant="warn" class="warning">${ESCROW_WARNING}</pk-alert>
            ${error ? html`<pk-alert variant="error" class="form-error" role="alert">${error}</pk-alert>` : nothing}
            ${this._done ? html`<p class="done" role="status">The encrypted escrow copy was prepared in your browser.</p>` : nothing}
            <form class="escrow" autocomplete="off" novalidate>
                <label class="field">
                    <span>Private key (PEM)</span>
                    <textarea data-field="secret" rows="5" spellcheck="false" placeholder="-----BEGIN PRIVATE KEY-----"></textarea>
                </label>
                <label class="field file">
                    <span>…or choose the key file</span>
                    <input data-field="file" type="file" accept=".pem,.key,.txt" @change=${this._onFile} />
                </label>
                <label class="field">
                    <span>Recovery password (at least ${MIN_RECOVERY_PASSWORD_LENGTH} characters)</span>
                    <input data-field="password" type="password" autocomplete="new-password" minlength=${String(MIN_RECOVERY_PASSWORD_LENGTH)} />
                </label>
                <label class="field">
                    <span>Confirm the recovery password</span>
                    <input data-field="confirm" type="password" autocomplete="new-password" />
                </label>
                <pk-button variant="primary" type="submit" ?loading=${this._busy} data-submitting-label="Encrypting…">${this.getAttribute('submit-label') ?? 'Store the escrow copy'}</pk-button>
            </form>
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkEscrowPasswordForm.tagName)) {
    customElements.define(PkEscrowPasswordForm.tagName, PkEscrowPasswordForm);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-escrow-password-form': PkEscrowPasswordForm;
    }
}
